import { Components } from '@mui/material'
import GeomanistBlack from '../../assets/fonts/geomanist-black-webfont.woff2'
import GeomanistBold from '../../assets/fonts/geomanist-bold-webfont.woff2'
import GeomanistLight from '../../assets/fonts/geomanist-light-webfont.woff2'
import GeomanistMedium from '../../assets/fonts/geomanist-medium-webfont.woff2'
import GeomanistRegular from '../../assets/fonts/geomanist-regular-webfont.woff2'
import shadows from './shadows'
import typography from './typography'
import palette from './palette'

const components: Components = {
  MuiCssBaseline: {
    styleOverrides: `
      @font-face {
        font-family: 'Geomanist';
        font-style: normal;
        font-display: swap;
        font-weight: 700;
        src: local('Geomanist'), url(${GeomanistBlack}) format('woff2');
        unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
      }
      @font-face {
        font-family: 'Geomanist';
        font-style: normal;
        font-display: swap;
        font-weight: 600;
        src: local('Geomanist'), url(${GeomanistBold}) format('woff2');
        unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
      }
      @font-face {
        font-family: 'Geomanist';
        font-style: normal;
        font-display: swap;
        font-weight: 500;
        src: local('Geomanist'), url(${GeomanistMedium}) format('woff2');
        unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
      }
      @font-face {
        font-family: 'Geomanist';
        font-style: normal;
        font-display: swap;
        font-weight: 400;
        src: local('Geomanist'), url(${GeomanistRegular}) format('woff2');
        unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
      }
      @font-face {
        font-family: 'Geomanist';
        font-style: normal;
        font-display: swap;
        font-weight: 100;
        src: local('Geomanist'), url(${GeomanistLight}) format('woff2');
        unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
      }
    `,
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: '2px',
        textTransform: 'capitalize',
        boxShadow: 'none',
        '&.active': {
          textUnderlineOffset: 4,
          textDecorationThickness: 2,
          textDecorationLine: 'underline',
          textDecorationColor: palette.background.primary,
        },
      },
      contained: {
        ':disabled': {
          color: palette.text.secondary,
          backgroundColor: '#F4F4F4',
        },
      },
      disableElevation: {
        color: palette.text.secondary,
        backgroundColor: '#F4F4F4',
        ':hover': {
          color: '#F4F4F4',
          backgroundColor: palette.text.secondary,
        },
      },
      sizeMedium: {
        height: '40px',
      },
      sizeSmall: {
        height: '32px',
      },
      outlined: {
        borderColor: palette.primary[600],
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: '4px',
        height: 'fit-content',
        '& .MuiOutlinedInput-input.MuiInputBase-inputSizeSmall': {
          padding: '8px 8px 8px 12px',
          ...typography.body3,
          height: '24px',
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        boxShadow: shadows[5],
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      root: {
        '&.MuiInputBase-sizeSmall > .MuiInputBase-input': {
          ...typography.bodyLarge,
        },
      },
    },
  },
  MuiPaginationItem: {
    styleOverrides: {
      root: {
        '&.Mui-selected': {
          color: palette.gray[0],
          backgroundColor: palette.primary[500],
          '&:hover': {
            backgroundColor: palette.primary[600],
          },
        },
      },
      sizeSmall: {
        ...typography.body1,
        color: palette.gray[700],
        height: '30px',
        minWidth: '30px',
      },
      previousNext: ({ ownerState }) => ({
        ...(ownerState.type === 'previous' && {
          marginRight: 21,
        }),
        ...(ownerState.type === 'next' && {
          marginLeft: 21,
        }),
        '& .MuiPaginationItem-icon': {
          height: 24,
          width: 24,
        },
      }),
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        padding: '8px',
        color: palette.gray[600],
        ...typography.caption,
        backgroundColor: palette.primary[50],
        borderColor: palette.primary[100],
        borderWidth: '1px',
        borderStyle: 'solid',
        boxShadow: '0px 8px 16px 6px rgba(33, 123, 235, 0.08)',
        borderRadius: '8px',
        width: 'fit-content',
      },
      arrow: {
        color: palette.primary[50],
        boxShadow: '0px 8px 16px 6px rgba(33, 123, 235, 0.08)',
        '&::before': {
          border: '1px solid',
          borderColor: palette.primary[100],
          backgroundColor: palette.primary[50],
          boxShadow: '0px 8px 16px 6px rgba(33, 123, 235, 0.08)',
        },
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        padding: '20px 20px 0px 20px',
        ...typography.body2,
        '@media (min-width: 600px)': {
          ...typography.body2,
        },
        '@media (min-width: 900px)': {
          ...typography.body2,
        },
        '@media (min-width: 1200px)': {
          ...typography.body2,
        },
        minHeight: '45px',
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: '0px 20px 24px 20px',
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '&.Mui-disabled': {
          opacity: 0.5,
        },
      },
    },
  },
}

export default components
